import { useState, useEffect } from 'react'
import { Link } from 'react-router'
import { useCookieConsentStore } from '@/stores/cookie-consent.store'

export function CookieSettingsPage() {
  const { preferences, consentDate, savePreferences, acceptAll, rejectOptional } =
    useCookieConsentStore()
  const [analyticsEnabled, setAnalyticsEnabled] = useState(preferences.analytics)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setAnalyticsEnabled(preferences.analytics)
  }, [preferences.analytics])

  const handleSave = () => {
    savePreferences({ analytics: analyticsEnabled })
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const handleAcceptAll = () => {
    acceptAll()
    setAnalyticsEnabled(true)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const handleRejectOptional = () => {
    rejectOptional()
    setAnalyticsEnabled(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not set'
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-3xl px-4">
        <div className="mb-8">
          <Link to="/" className="text-primary-600 hover:underline">
            &larr; Back to Home
          </Link>
        </div>

        <div className="rounded-lg bg-white p-8 shadow">
          <h1 className="mb-2 text-3xl font-bold">Cookie Settings</h1>
          <p className="mb-6 text-gray-600">
            Manage your cookie preferences. You can change these settings at any time.
          </p>

          {saved && (
            <div className="mb-6 rounded-lg bg-green-50 border border-green-200 p-4">
              <p className="text-sm text-green-700 font-medium">
                Your cookie preferences have been saved.
              </p>
            </div>
          )}

          {consentDate && (
            <div className="mb-6 rounded-lg bg-gray-50 p-4">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Last updated:</span> {formatDate(consentDate)}
              </p>
            </div>
          )}

          <div className="space-y-6">
            {/* Essential cookies */}
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900">Essential Cookies</h3>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                      Always Active
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    These cookies are necessary for the website to function and cannot be
                    switched off. They are usually only set in response to actions made by you,
                    such as logging in or filling in forms.
                  </p>
                  <details className="text-sm">
                    <summary className="cursor-pointer text-primary-600 hover:underline font-medium">
                      View cookies in this category
                    </summary>
                    <div className="mt-3 bg-gray-50 rounded-lg p-3">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="pb-2">Name</th>
                            <th className="pb-2">Purpose</th>
                            <th className="pb-2">Duration</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-700">
                          <tr>
                            <td className="py-1 font-mono">brightseed-auth</td>
                            <td className="py-1">Authentication session</td>
                            <td className="py-1">7 days</td>
                          </tr>
                          <tr>
                            <td className="py-1 font-mono">brightseed-cookie-consent</td>
                            <td className="py-1">Stores your cookie preferences</td>
                            <td className="py-1">1 year</td>
                          </tr>
                          <tr>
                            <td className="py-1 font-mono">brightseed-app-lock</td>
                            <td className="py-1">App security settings</td>
                            <td className="py-1">Persistent</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </details>
                </div>
                <div className="flex items-center pt-1">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="h-5 w-5 rounded border-gray-300 text-primary-600 cursor-not-allowed opacity-50"
                  />
                </div>
              </div>
            </div>

            {/* Analytics cookies */}
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900">Analytics Cookies</h3>
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        analyticsEnabled
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {analyticsEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    These cookies allow us to count visits and traffic sources so we can measure
                    and improve the performance of our site. They help us understand which pages
                    are the most popular and how visitors move around the site.
                  </p>
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>Privacy note:</strong> We never track the content of your messages,
                    updates, or any personal information about children. Analytics only track
                    feature usage patterns and errors.
                  </p>
                  <details className="text-sm">
                    <summary className="cursor-pointer text-primary-600 hover:underline font-medium">
                      View cookies in this category
                    </summary>
                    <div className="mt-3 bg-gray-50 rounded-lg p-3">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="pb-2">Name</th>
                            <th className="pb-2">Purpose</th>
                            <th className="pb-2">Duration</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-700">
                          <tr>
                            <td className="py-1 font-mono">ph_*</td>
                            <td className="py-1">PostHog analytics - anonymous usage data</td>
                            <td className="py-1">1 year</td>
                          </tr>
                        </tbody>
                      </table>
                      <p className="mt-2 text-gray-500">
                        Provider:{' '}
                        <a
                          href="https://posthog.com/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline"
                        >
                          PostHog
                        </a>
                      </p>
                    </div>
                  </details>
                </div>
                <div className="flex items-center pt-1">
                  <button
                    role="switch"
                    aria-checked={analyticsEnabled}
                    onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                      analyticsEnabled ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        analyticsEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSave}
              className="px-6 py-3 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              Save Preferences
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Accept All
            </button>
            <button
              onClick={handleRejectOptional}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reject All Optional
            </button>
          </div>

          {/* Additional info */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">More Information</h2>
            <p className="text-sm text-gray-600 mb-4">
              For more details about how we handle your data, please read our{' '}
              <Link to="/privacy" className="text-primary-600 hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
            <p className="text-sm text-gray-600">
              If you have any questions about cookies or your privacy, please contact us at{' '}
              <a
                href="mailto:support@brightseed.app"
                className="text-primary-600 hover:underline"
              >
                support@brightseed.app
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
